import { queryKeys } from '@tms/api-client';
import {
  AppBar,
  DayBar,
  EmptyState,
  EntryRow,
  EntryRowSkeleton,
  Fab,
  NavBar,
  PhoneShell,
  Pill,
  Select,
  defaultNavItems,
} from '@tms/ui';
import { formatMonth, recentPaydayMonths, toIstDate } from '@tms/core';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { getLedgerPage, getMe } from '../../api';
import type { LedgerEntry } from '../../api';
import { pathToEntry, routePaths } from '../../routes/routes';
import { ledgerCopy } from './copy';
import { dayLabel, groupByDay } from './grouping';
import styles from './LedgerScreen.module.css';

type KindFilter = 'all' | 'Debit' | 'Credit';

/**
 * `LED-05` — every entry, grouped by day, with the running balance on each day bar.
 *
 * The screen book calls that balance "the single thing a paper ledger does that most expense apps drop",
 * and it is why this screen is not just a longer version of Home.
 *
 * **Virtualised.** A person with two years of entries has a few thousand rows, and rendering them all is
 * how a list stops scrolling smoothly on the kind of phone this product is for (`ARCH-005 §2`). The day
 * bars are virtualised alongside the rows rather than being separate sections, because a header outside
 * the window is a header that vanishes when its rows do.
 *
 * **Filters do not refetch from scratch.** `keepPreviousData` holds the current list on screen while the
 * new one loads, so tapping "Debit" changes the rows rather than blanking the screen and rebuilding it.
 * That is `T-0216`'s second criterion, and it is one line rather than a cache of our own.
 */
export default function LedgerScreen(): React.JSX.Element {
  const navigate = useNavigate();
  const scroller = useRef<HTMLDivElement>(null);

  const [kind, setKind] = useState<KindFilter>('all');

  // `undefined` is all time, and it is the default: the eyebrow says "Every entry", and a screen that
  // silently opened on one month would contradict itself (`FEAT-011 §2.1`).
  const [month, setMonth] = useState<string | undefined>(undefined);

  // The payday cycle, for the month list. Cached for the session by `ONB-07`'s own read of it, so this is
  // not a second request in practice; while it is loading the control offers the calendar month, which is
  // what nine accounts in ten have anyway.
  const account = useQuery({
    queryKey: queryKeys.session.current(),
    queryFn: async () => {
      const result = await getMe();
      if (!result.ok) {
        throw result.failure;
      }

      return result.value;
    },
  });

  const months = recentPaydayMonths(toIstDate(new Date()), account.data?.monthStartsOnDay ?? 1, 12);

  const filters = {
    ...(kind === 'all' ? {} : { kind }),
    ...(month ? { month } : {}),
  };

  const ledger = useInfiniteQuery({
    queryKey: queryKeys.ledger.list(filters),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const result = await getLedgerPage({
        ...filters,
        ...(pageParam ? { cursor: pageParam } : {}),
      });

      if (!result.ok) {
        throw result.failure;
      }

      return result.value;
    },

    // The cursor is the server's and is opaque — the client hands back exactly what it was given
    // (`FEAT-011 §5.4`).
    getNextPageParam: (last) => last.nextCursor ?? undefined,

    // The list stays on screen while a filter change loads. Blanking and rebuilding would lose the scroll
    // position too, which on a ledger means losing your place.
    placeholderData: keepPreviousData,
  });

  const entries = (ledger.data?.pages ?? []).flatMap((page) => page.items ?? []);

  // Day bars and rows in one flat list, so the virtualiser measures both and a header cannot outlive the
  // rows it belongs to.
  const rows = groupByDay(entries);

  const virtualiser = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scroller.current,

    // Two **exact** heights, not estimates: a day bar is one line and an entry row's title truncates to
    // one line by design (`EntryRow`), so neither varies. That is why nothing here measures the rendered
    // elements — a measured height is only worth its cost when the height can surprise you, and remeasuring
    // fixed rows is jitter in the scrollbar for nothing.
    estimateSize: (index) => (rows[index]?.type === 'day' ? 34 : 64),
    overscan: 8,

    // A window to work with before the scroller has been measured. On a device this saves one frame of an
    // empty list; in a test environment, where nothing has a layout at all, it is the difference between
    // rendering the ledger and rendering one row — which is also what made the "far fewer nodes" assertion
    // below pass while proving nothing.
    initialRect: { width: 411, height: 900 },
  });

  return (
    <PhoneShell
      appBar={<AppBar title={ledgerCopy.title} />}
      fab={
        <Fab
          label={ledgerCopy.addEntry}
          data-testid="add-entry"
          onPress={() => {
            void navigate(routePaths.addEntry);
          }}
        />
      }
      navBar={
        <NavBar
          items={defaultNavItems}
          currentId="ledger"
          onNavigate={(next) => {
            if (next === 'home') {
              void navigate(routePaths.home);
            }
          }}
        />
      }
    >
      <p className={styles.eyebrow}>{ledgerCopy.eyebrow}</p>

      <div className={styles.filters}>
        <div className={styles.chips} role="group" aria-label={ledgerCopy.filterLabel}>
          {(['all', 'Credit', 'Debit'] as const).map((option) => (
            <Pill
              key={option}
              selected={kind === option}
              onSelect={() => {
                setKind(option);
              }}
            >
              {option === 'all' ? ledgerCopy.all : option === 'Credit' ? ledgerCopy.credit : ledgerCopy.debit}
            </Pill>
          ))}
        </div>

        {/*
          The label is hidden because the control's own value says "August", and a "Month" beside it would
          say it twice to anybody who can see it and once too often to anybody who cannot.
        */}
        <Select
          label={ledgerCopy.monthLabel}
          labelHidden
          data-testid="ledger-month"
          value={month ?? ''}
          onChange={(event) => {
            setMonth(event.target.value === '' ? undefined : event.target.value);
          }}
        >
          <option value="">{ledgerCopy.allTime}</option>
          {months.map((value) => (
            <option key={value} value={value}>
              {formatMonth(Number(value.slice(0, 4)), Number(value.slice(5, 7)))}
            </option>
          ))}
        </Select>
      </div>

      {ledger.isPending ? (
        <EntryRowSkeleton count={6} />
      ) : ledger.isError ? (
        <p className={styles.error} role="status">
          {ledgerCopy.failed}
        </p>
      ) : entries.length === 0 ? (
        <EmptyState title={ledgerCopy.emptyTitle} body={ledgerCopy.emptyBody} />
      ) : (
        <div ref={scroller} className={styles.scroller} data-testid="ledger-scroller">
          <div className={styles.window} style={{ height: `${String(virtualiser.getTotalSize())}px` }}>
            {virtualiser.getVirtualItems().map((item) => {
              const row = rows[item.index];
              if (!row) {
                return null;
              }

              return (
                <div
                  key={item.key}
                  className={styles.item}
                  style={{ transform: `translateY(${String(item.start)}px)`, height: `${String(item.size)}px` }}
                  data-index={item.index}
                >
                  {row.type === 'day' ? (
                    <DayBar
                      label={dayLabel(row.occurredOn)}
                      {...(row.balanceMinor === undefined ? {} : { balanceMinor: row.balanceMinor })}
                    />
                  ) : (
                    <EntryRow
                      icon={row.entry.category.icon}
                      title={titleOf(row.entry)}
                      detail={row.entry.category.name}
                      amountMinor={row.entry.amountMinor}
                      kind={row.entry.kind === 'Credit' ? 'credit' : 'debit'}
                      onPress={() => {
                        void navigate(pathToEntry(row.entry.id));
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {ledger.hasNextPage ? (
        <button
          type="button"
          className={styles.more}
          disabled={ledger.isFetchingNextPage}
          data-testid="ledger-more"
          onClick={() => {
            void ledger.fetchNextPage();
          }}
        >
          {ledger.isFetchingNextPage ? ledgerCopy.loadingMore : ledgerCopy.more}
        </button>
      ) : null}
    </PhoneShell>
  );
}

/** The note if there is one, and the purpose if there is not — as `LED-01` does. */
function titleOf(entry: LedgerEntry): string {
  return entry.note?.trim() ? entry.note : entry.category.name;
}
